import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '@/lib/api-client';

interface BookingDetails {
  id: string;
  status: string;
  customer_name: string;
  customer_email: string;
  booking_date: string;
  booking_time: string;
  duration_minutes: number;
  total_price: number | null;
  services: Array<{ name: string; duration: number; price: number }>;
  profile: { business_name: string };
}

type CancelStatus = 'idle' | 'loading' | 'success' | 'error';

const CancelBooking: React.FC = () => {
  const { t } = useTranslation('guest');
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [booking, setBooking] = useState<BookingDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelStatus, setCancelStatus] = useState<CancelStatus>('idle');
  const [cancelError, setCancelError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBooking = async () => {
      if (!token) return;

      setIsLoading(true);
      setError(null);

      try {
        const response = await apiClient.get<{
          success: boolean;
          data: BookingDetails;
        }>(`/guest/bookings/${token}`);
        setBooking(response.data.data);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load booking details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBooking();
  }, [token]);

  const handleCancel = async () => {
    if (!token) return;

    setCancelStatus('loading');
    setCancelError(null);

    try {
      await apiClient.delete(`/guest/bookings/${token}`);
      setCancelStatus('success');
    } catch (err: any) {
      setCancelError(err.response?.data?.message || 'Failed to cancel booking');
      setCancelStatus('error');
    }
  };

  const handleGoBack = () => {
    navigate('/');
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (timeString: string): string => {
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours, 10), parseInt(minutes, 10));
    return date.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-gray-600">{t('cancel.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="mt-4 text-xl font-medium text-gray-900">{t('cancel.notFound')}</h2>
          <p className="mt-2 text-gray-600">{error || t('cancel.notFoundDescription')}</p>
          <button
            onClick={() => navigate('/')}
            className="mt-6 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {t('cancel.goHome')}
          </button>
        </div>
      </div>
    );
  }

  if (cancelStatus === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="mt-4 text-xl font-medium text-gray-900">{t('cancel.successTitle')}</h2>
          <p className="mt-2 text-gray-600">{t('cancel.successMessage')}</p>
          <button
            onClick={() => navigate('/')}
            className="mt-6 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {t('cancel.goHome')}
          </button>
        </div>
      </div>
    );
  }

  const serviceNames = booking.services.map((s) => s.name).join(', ');

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full mx-auto px-4">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h1 className="mt-4 text-xl font-semibold text-gray-900">{t('cancel.title')}</h1>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">{t('cancel.bookingDetails')}</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">{t('cancel.service')}</span>
                <span className="text-gray-900 font-medium">{serviceNames}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('cancel.date')}</span>
                <span className="text-gray-900 font-medium">{formatDate(booking.booking_date)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('cancel.time')}</span>
                <span className="text-gray-900 font-medium">{formatTime(booking.booking_time)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('cancel.duration')}</span>
                <span className="text-gray-900 font-medium">{booking.duration_minutes} min</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('cancel.provider')}</span>
                <span className="text-gray-900 font-medium">{booking.profile.business_name}</span>
              </div>
            </div>
          </div>

          <p className="text-center text-gray-600 mb-6">{t('cancel.confirmMessage')}</p>

          {cancelStatus === 'error' && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600 text-center">{cancelError}</p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={handleGoBack}
              disabled={cancelStatus === 'loading'}
              className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              {t('cancel.goBack')}
            </button>
            <button
              onClick={handleCancel}
              disabled={cancelStatus === 'loading'}
              className="flex-1 px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {cancelStatus === 'loading' ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {t('cancel.cancelling')}
                </>
              ) : (
                t('cancel.confirmCancel')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancelBooking;
